/* A dense table on a 390px screen — a port of `MobileTable.dc.html`.
 *
 * The trick the design settled on: don't reflow the table into cards, and
 * don't shrink the type. Keep the real columns at their real widths, put
 * them in a horizontal scroller, and pin the first one so a row you have
 * swiped sideways is still identifiable. Tapping a header sorts by that
 * column — descending first for a number, ascending for the pinned label,
 * then the other way, then back to the register's own order.
 *
 * The first column is the row's name and is set in the condensed heading
 * face at 14.5px; everything after it is a figure in the body face at
 * 12.5px, tabular. A cell may carry a sub-label under its value and a
 * share bar beside it.
 */

import { useMemo, useState } from 'react'

export interface MCol {
  /** Header text. */
  l: string
  /** Width in pixels — real widths, not fractions: the table is allowed to
   *  be wider than the screen. */
  w: number
  /** Cells are right-aligned except the first; 'left' overrides that for a
   *  column of text, like a station or a department. */
  al?: 'left' | 'right'
}

export interface MCell {
  v: string
  /** Second line under the value, in the muted face. */
  sub?: string
  color?: string
  /** 0–100; draws a share bar before the value. */
  bar?: number
  barC?: string
  /** Sort key, when the displayed string is not what should be compared. */
  s?: number | string
}

export interface MRow {
  cells: MCell[]
  /** Makes the row tappable. */
  on?: () => void
  /** Marks the row as the one the screen is showing. */
  sel?: boolean
}

/** What a cell sorts by: its own `s`, else the number inside its text, else
 *  the text. The minus sign the app formats with is U+2212, not a hyphen,
 *  so it has to be translated before parseFloat sees it. */
function key(c: MCell, i: number): number | string {
  if (c.s != null) return c.s
  const s = String(c.v)
  if (i === 0) return s.toLowerCase()
  const m = s.replace(/−/g, '-').replace(/,/g, '').match(/-?\d+(\.\d+)?/)
  return m ? parseFloat(m[0]) : -Infinity
}

export function MobileTable({ cols, rows, hint }: {
  cols: MCol[]
  rows: MRow[]
  /** Shown under the table when it is wider than the screen. */
  hint?: string
}) {
  const [sort, setSort] = useState<{ col: number; dir: 1 | -1 } | null>(null)

  const sorted = useMemo(() => {
    if (!sort) return rows
    const { col, dir } = sort
    return rows.slice().sort((a, b) => {
      const x = key(a.cells[col], col)
      const y = key(b.cells[col], col)
      if (typeof x === 'string' || typeof y === 'string') {
        return String(x).localeCompare(String(y)) * dir
      }
      return (x - y) * dir
    })
  }, [rows, sort])

  /** Three states per column: the natural direction, the other one, then
   *  off — so there is always a way back to the register's own order. */
  const toggle = (i: number) => () => setSort((s) => {
    const natural: 1 | -1 = i === 0 ? 1 : -1
    if (!s || s.col !== i) return { col: i, dir: natural }
    if (s.dir === natural) return { col: i, dir: (natural === 1 ? -1 : 1) as 1 | -1 }
    return null
  })

  const gtc = cols.map((c) => `${c.w}px`).join(' ')
  const total = cols.reduce((s, c) => s + c.w, 0)
  const alignOf = (i: number) =>
    ((cols[i]?.al ?? (i ? 'right' : 'left')) === 'left' ? 'flex-start' : 'flex-end')

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      <div className="m-table-wrap m-noscroll">
        <div className="m-table">
          <div className="m-table-row" style={{ gridTemplateColumns: gtc }}>
            {cols.map((c, i) => {
              const on = sort?.col === i
              return (
                <button
                  key={c.l + i}
                  type="button"
                  className={'m-th' + (i ? '' : ' m-sticky')}
                  onClick={toggle(i)}
                  aria-sort={on ? (sort.dir === -1 ? 'descending' : 'ascending') : undefined}
                  style={{ justifyContent: alignOf(i) }}
                >
                  <span>{c.l}</span>
                  <span style={{ fontSize: 10, letterSpacing: 0, opacity: on ? 1 : 0.45 }}>
                    {on ? (sort.dir === -1 ? '↓' : '↑') : '↕'}
                  </span>
                </button>
              )
            })}
          </div>

          {sorted.map((r, ri) => {
            const bg = r.sel ? 'var(--color-accent-100)' : 'var(--color-bg)'
            const Row = r.on ? 'button' : 'div'
            return (
              <Row
                key={ri}
                {...(r.on ? { type: 'button' as const, onClick: r.on } : {})}
                className="m-table-row"
                style={{
                  gridTemplateColumns: gtc,
                  border: 0, padding: 0, background: 'transparent',
                  font: 'inherit', textAlign: 'left',
                  color: 'var(--color-text)',
                  cursor: r.on ? 'pointer' : 'default',
                  width: '100%',
                }}
              >
                {r.cells.map((c, i) => (
                  <div
                    key={i}
                    className={'m-td' + (i ? '' : ' m-sticky')}
                    style={{ alignItems: alignOf(i), background: bg }}
                  >
                    <div style={{
                      display: 'flex', alignItems: 'center', gap: 6, maxWidth: '100%',
                    }}>
                      {c.bar != null && (
                        <span className="m-cell-bar">
                          <span style={{
                            width: `${Math.max(0, Math.min(100, c.bar))}%`,
                            background: c.barC ?? 'var(--c-bar1)',
                          }} />
                        </span>
                      )}
                      <span
                        className={i ? 'm-num' : 'm-num m-ellipsis'}
                        style={{
                          flex: i ? 'none' : '0 1 auto',
                          fontFamily: i ? 'inherit' : 'var(--font-heading)',
                          fontSize: i ? 12.5 : 14.5,
                          fontWeight: i ? 400 : 600,
                          color: c.color ?? 'var(--color-text)',
                          whiteSpace: 'nowrap',
                        }}
                      >{c.v}</span>
                    </div>
                    {c.sub ? (
                      <span className="m-ellipsis" style={{
                        fontSize: 10.5, color: 'var(--color-neutral-600)', maxWidth: '100%',
                      }}>{c.sub}</span>
                    ) : null}
                  </div>
                ))}
              </Row>
            )
          })}
        </div>
      </div>
      {total > 360 && (
        <div className="m-aside" style={{ textAlign: 'right' }}>
          {hint ?? 'Swipe for more columns →'}
        </div>
      )}
    </div>
  )
}
